import { DatePicker, Form, Input, Select, Space } from "antd";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import React, { useRef, useEffect } from "react";
import { useController, useWatch } from "react-hook-form";
import {
  dateFormat,
  toEndOfDay,
  toStartOfDay,
  unspecifiedTZFormat,
} from "../../formatting";
import { fixedDateRangeOptions, fixedDateRanges } from "../fixedDateRanges";
import { checkHookFormFieldPropsEquality } from "../formValidatonRules";
dayjs.extend(quarterOfYear);

const AdvancedRangePicker = (props) => {
  const {
    name,
    dateRangeConfig,
    setValue,
    control,
    formItem,
    onChange,
    afterChange,
    useDynamicName = false,
    toDateEndingIsYesterday,
  } = props;

  const fixedDateRangeControlName = useDynamicName
    ? name + "FixedDateRange"
    : "FixedDateRange";
  const numberOfDaysControlName = useDynamicName
    ? name + "NumberOfDays"
    : "NumberOfDays";

  const fixedDateRange = useWatch({
    control,
    name: fixedDateRangeControlName,
  });
  const numberOfDays = useWatch({
    control,
    name: numberOfDaysControlName,
  });

  const fixedDateRangeRef = useRef(fixedDateRange);
  const numberOfDaysRef = useRef(numberOfDays);

  const {
    field,
    fieldState: { error },
  } = useController({
    name: name,
    control: control,
  });

  const showNumberOfDaysValues = [
    fixedDateRanges.IN_X_DAYS,
    fixedDateRanges.X_DAYS_AGO,
    fixedDateRanges.LAST_X_DAYS,
    fixedDateRanges.NEXT_X_DAYS,
  ];

  const getCurrentPeriodRange = (periodName) => [
    dayjs().startOf(periodName).format(unspecifiedTZFormat),
    dayjs().endOf(periodName).format(unspecifiedTZFormat),
  ];

  const getPeriodToDateRange = (periodName) => {
    const useYesterday = toDateEndingIsYesterday ?? false;
    const endDate = useYesterday ? dayjs().subtract(1, "day") : dayjs();

    return [
      dayjs().startOf(periodName).format(unspecifiedTZFormat),
      endDate.endOf("day").format(unspecifiedTZFormat),
    ];
  };

  const getLastPeriodRange = (periodName) => [
    dayjs()
      .subtract(1, periodName)
      .startOf(periodName)
      .format(unspecifiedTZFormat),
    dayjs()
      .subtract(1, periodName)
      .endOf(periodName)
      .format(unspecifiedTZFormat),
  ];

  const getNextPeriodRange = (periodName) => [
    dayjs().add(1, periodName).startOf(periodName).format(unspecifiedTZFormat),
    dayjs().add(1, periodName).endOf(periodName).format(unspecifiedTZFormat),
  ];

  const setValueEx = (valueName, values, extras) => {
    onChange && onChange(values);
    setValue(valueName, values, extras);
    if (props.afterChange != null) {
      props.afterChange(values);
    }
  };

  const setCustomRange = (days, rangeType) => {
    const parseDayCount = (d) => {
      const num = Number(d);
      if (isNaN(num) || d === "" || num < 0) return 0;
      if (num > 735) return 735;
      return num;
    };

    const parsedDays = parseDayCount(days);

    switch (rangeType) {
      case fixedDateRanges.IN_X_DAYS:
        setValueEx(
          name,
          [
            dayjs()
              .add(parsedDays, "day")
              .startOf("day")
              .format(unspecifiedTZFormat),
            dayjs()
              .add(parsedDays, "day")
              .endOf("day")
              .format(unspecifiedTZFormat),
          ],
          { shouldValidate: true, shouldDirty: true }
        );
        break;
      case fixedDateRanges.X_DAYS_AGO:
        setValueEx(
          name,
          [
            dayjs()
              .subtract(parsedDays, "day")
              .startOf("day")
              .format(unspecifiedTZFormat),
            dayjs()
              .subtract(parsedDays, "day")
              .endOf("day")
              .format(unspecifiedTZFormat),
          ],
          { shouldValidate: true, shouldDirty: true }
        );
        break;
      case fixedDateRanges.NEXT_X_DAYS:
        setValueEx(
          name,
          parsedDays <= 0
            ? [
                dayjs().startOf("day").format(unspecifiedTZFormat),
                dayjs().endOf("day").format(unspecifiedTZFormat),
              ]
            : [
                dayjs()
                  .add(1, "day")
                  .startOf("day")
                  .format(unspecifiedTZFormat),
                dayjs()
                  .add(Math.max(1, parsedDays), "day")
                  .endOf("day")
                  .format(unspecifiedTZFormat),
              ],
          { shouldValidate: true, shouldDirty: true }
        );
        break;
      case fixedDateRanges.LAST_X_DAYS:
        setValueEx(
          name,
          parsedDays <= 1
            ? [
                dayjs().startOf("day").format(unspecifiedTZFormat),
                dayjs().endOf("day").format(unspecifiedTZFormat),
              ]
            : [
                dayjs()
                  .subtract(parsedDays - 1, "day")
                  .startOf("day")
                  .format(unspecifiedTZFormat),
                dayjs().endOf("day").format(unspecifiedTZFormat),
              ],
          { shouldValidate: true, shouldDirty: true }
        );
        break;
      case fixedDateRanges.ALL_DATES:
        setValueEx(name, [null, null], {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fixedDateRangeRef.current = fixedDateRange;
  }, [fixedDateRange]);

  useEffect(() => {
    numberOfDaysRef.current = numberOfDays;
  }, [numberOfDays]);

  useEffect(() => {
    if (!fixedDateRange || toDateEndingIsYesterday === null) return;

    onFixedRangeSelect(fixedDateRange);
  }, [fixedDateRange, toDateEndingIsYesterday]);

  const onFixedRangeSelect = (value) => {
    switch (value) {
      case fixedDateRanges.TODAY:
        setValueEx(name, getCurrentPeriodRange("day"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.YESTERDAY:
        setValueEx(name, getLastPeriodRange("day"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.IN_X_DAYS:
      case fixedDateRanges.X_DAYS_AGO:
      case fixedDateRanges.NEXT_X_DAYS:
      case fixedDateRanges.LAST_X_DAYS:
        setCustomRange(numberOfDaysRef.current, value);
        break;
      case fixedDateRanges.THIS_MONTH:
        setValueEx(name, getCurrentPeriodRange("month"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.NEXT_MONTH:
        setValueEx(name, getNextPeriodRange("month"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.LAST_MONTH:
        setValueEx(name, getLastPeriodRange("month"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.MTD:
        setValueEx(name, getPeriodToDateRange("month"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.QTD:
        setValueEx(name, getPeriodToDateRange("quarter"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.THIS_QUARTER:
        setValueEx(name, getCurrentPeriodRange("quarter"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.LAST_QUARTER:
        setValueEx(name, getLastPeriodRange("quarter"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.NEXT_QUARTER:
        setValueEx(name, getNextPeriodRange("quarter"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.THIS_YEAR:
        setValueEx(name, getCurrentPeriodRange("year"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.LAST_YEAR:
        setValueEx(name, getLastPeriodRange("year"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.YTD:
        setValueEx(name, getPeriodToDateRange("year"), {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      case fixedDateRanges.ALL_DATES:
        setValueEx(name, [null, null], {
          shouldValidate: true,
          shouldDirty: true,
        });
        break;
      default:
        // code block
        break;
    }
  };

  return (
    <Form.Item
      {...formItem}
      {...formItem?.layout}
      name={name}
      validateStatus={error != null ? "error" : ""}
      help={error != null ? error.message : null}
    >
      <Space size={8} style={{ height: 24 }}>
        <FixedDateRangeSelection
          name={fixedDateRangeControlName}
          control={control}
          dateRangeConfig={dateRangeConfig}
          onFixedRangeSelect={onFixedRangeSelect}
        />
        {fixedDateRange && showNumberOfDaysValues.includes(fixedDateRange) && (
          <NumberOfDays
            name={numberOfDaysControlName}
            control={control}
            onChangeNumberOfDays={(e) => {
              setCustomRange(e.target.value, fixedDateRangeRef.current);
            }}
          />
        )}

        {fixedDateRange !== fixedDateRanges.ALL_DATES && (
          <DatePicker.RangePicker
            ref={field.ref}
            allowClear={false}
            disabledDate={props.disabledDate}
            format={dateFormat}
            value={[
              field?.value?.[0] ? dayjs(field.value[0]) : "",
              field?.value?.[1] ? dayjs(field.value[1]) : "",
            ]}
            onChange={(dates) => {
              var transformedDates = [
                toStartOfDay(dates?.[0] ?? ""),
                toEndOfDay(dates?.[1] ?? ""),
              ];
              dates && onChange && onChange(transformedDates);
              field.onChange(transformedDates);
              dates && afterChange && afterChange(transformedDates);
              setValue(fixedDateRangeControlName, fixedDateRanges.NONE);
            }}
          />
        )}
      </Space>
    </Form.Item>
  );
};

export default AdvancedRangePicker;

const FixedDateRangeSelection = (props) => {
  const {
    field,
    fieldState: { error, isTouched, isDirty },
  } = useController({
    name: props.name,
    control: props.control,
  });

  return (
    <FixedDateRangeSelectionContent
      {...props}
      field={field}
      error={error}
      isTouched={isTouched}
      isDirty={isDirty}
    >
      {props.children}
    </FixedDateRangeSelectionContent>
  );
};

const FixedDateRangeSelectionContent = React.memo((props) => {
  return (
    <span
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Select
        {...props.field}
        name={props.name}
        options={fixedDateRangeOptions(props.dateRangeConfig)}
        style={{ width: 152 }}
        allowClear={props.allowClear}
        placeholder="Fixed range"
        onSelect={props.onFixedRangeSelect}
        showSearch
        virtual={false}
        optionFilterProp="label"
        filterOption={(input, option) => {
          const label = option.label ? option.label : option.children;
          return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }}
      />
    </span>
  );
}, checkHookFormFieldPropsEquality);

const NumberOfDays = (props) => {
  const {
    field,
    fieldState: { error, isTouched, isDirty },
  } = useController({
    name: props.name,
    control: props.control,
  });

  return (
    <NumberOfDaysContent
      {...props}
      field={field}
      error={error}
      isTouched={isTouched}
      isDirty={isDirty}
    >
      {props.children}
    </NumberOfDaysContent>
  );
};

const NumberOfDaysContent = React.memo((props) => {
  return (
    <Input
      {...props.field}
      name={props.name}
      style={{ width: 40 }}
      maxLength={3}
      // type="number"
      onChange={(e) => {
        props.field.onChange(e);
        props.onChangeNumberOfDays(e);
      }}
    />
  );
}, checkHookFormFieldPropsEquality);
